import React, { useState, useEffect } from 'react';
import RecommendationsProductList from '../RecommendationsProductList';
import PropTypes from 'prop-types';
import { raygun } from '@ynap/clientside-monitoring';
import { useConfig, useAppState } from '@ynap/immutables-provider';
import { safeBuildEventObj } from '@ynap/analytics';
import { fetchProductDetails } from '../fetch-utils';
import { useToRecommendedProduct } from '../use-to-recommended-product';
import { trackRemoveRecentlyViewedItem } from '../../analytics';

const configSelector = (config) => ({
    availableImageSizes: config?.head?.availableImageSizes,
    baseUrl: config?.global.services.commerce.baseUrl,
    brandId: config?.global.application.brandId,
    clientId: config?.global.services.commerce.clientId,
    imageQuality: config?.head?.imageQualityPercentage,
    pdpUrlTemplate: config?.global?.urlConfig?.pdp?.construct,
});

const RecentlyViewedCarousel = ({
    messages,
    colourPartNumber,
    modelPartNumber,
    tracking,
    isCarouselVisible,
    isFullWidth,
    addToCart,
    isWithAddToBag,
    className,
    useDefaultCarouselIndex,
    withQuickAdd,
    variation,
    reducedMode,
    carouselConfig,
}) => {
    const { baseUrl, clientId, brandId } = useConfig(configSelector);
    const { country, langISO } = useAppState();
    const [recentlyViewedProducts, setRecentlyViewedProducts] = useState([]);
    const toRecommendedProduct = useToRecommendedProduct();

    useEffect(() => {
        const getRecentlyViewedProducts = async () => {
            try {
                const recentlyViewedStoredProducts = JSON.parse(localStorage.getItem('recentlyViewedPartNumbers')) || [];
                const filteredPartNumbers = recentlyViewedStoredProducts.filter((partNumbers) => {
                    const rvModelPartNumber = Array.isArray(partNumbers) && partNumbers[0];
                    return rvModelPartNumber && rvModelPartNumber !== modelPartNumber;
                });

                if (filteredPartNumbers.length) {
                    const productsPromise = filteredPartNumbers.map((partNumbers) => async () => {
                        const partNumber = Array.isArray(partNumbers) && (partNumbers[1] ?? partNumbers[0]);
                        if (!partNumber) return null;

                        const productDetails = await fetchProductDetails({ partNumber, baseUrl, brandId, clientId, country, langISO });
                        return productDetails?.displayable ? productDetails : null;
                    });

                    Promise.all(productsPromise.map((promise) => promise()))
                        .then((products) => products.filter(Boolean).map(toRecommendedProduct))
                        .then(setRecentlyViewedProducts);
                }
            } catch (error) {
                raygun.helpers.agent('send', {
                    error,
                    tags: ['PDP', 'recentlyViewed'],
                });
            }
        };
        getRecentlyViewedProducts();
    }, []);

    if (!recentlyViewedProducts || recentlyViewedProducts.length === 0) return null;

    const removeRecentlyViewedProduct = (product) => {
        const { partNumber: partNumberToRemove, price: { finalPrice } = {} } = product;
        const recentlyViewedStoredProducts = JSON.parse(localStorage.getItem('recentlyViewedPartNumbers')) || [];
        const updatedPartNumbers = recentlyViewedStoredProducts.filter((partNumbers) => partNumbers[1] !== partNumberToRemove);
        localStorage.setItem('recentlyViewedPartNumbers', JSON.stringify(updatedPartNumbers));

        tracking?.trackEvent(safeBuildEventObj(trackRemoveRecentlyViewedItem, partNumberToRemove, finalPrice));

        setRecentlyViewedProducts((prevProducts) => {
            return prevProducts.filter((product) => product.partNumber !== partNumberToRemove);
        });
    };

    return (
        <RecommendationsProductList
            carouselConfig={carouselConfig}
            messages={messages}
            heading={messages?.recentlyViewed?.heading}
            colourPartNumber={colourPartNumber}
            recommendations={recentlyViewedProducts}
            onClick={tracking.rv}
            isCarouselVisible={isCarouselVisible}
            type={'rv'}
            tracking={tracking}
            isFullWidth={isFullWidth}
            addToCart={addToCart}
            isWithAddToBag={isWithAddToBag}
            className={className}
            useDefaultCarouselIndex={useDefaultCarouselIndex}
            withQuickAdd={withQuickAdd}
            variation={variation}
            reducedMode={reducedMode}
            onRemove={removeRecentlyViewedProduct}
        />
    );
};

RecentlyViewedCarousel.propTypes = {
    messages: PropTypes.object,
    colourPartNumber: PropTypes.string.isRequired,
    modelPartNumber: PropTypes.string.isRequired,
    tracking: PropTypes.object,
    isCarouselVisible: PropTypes.bool,
    isFullWidth: PropTypes.bool,
    addToCart: PropTypes.func,
    isWithAddToBag: PropTypes.bool,
    className: PropTypes.string,
    useDefaultCarouselIndex: PropTypes.bool,
    withQuickAdd: PropTypes.bool,
    variation: PropTypes.string,
    reducedMode: PropTypes.bool,
    carouselConfig: PropTypes.object,
    baseUrl: PropTypes.string.isRequired,
    brandId: PropTypes.string.isRequired,
    clientId: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    langISO: PropTypes.string.isRequired,
    toRecommendedProduct: PropTypes.func.isRequired,
    fetchProductDetails: PropTypes.func.isRequired,
};

export default RecentlyViewedCarousel;
